/**
 * The org shape, reviewed with the promo team in Sep 2026: IRC-aligned
 * regions over RC areas over cities.
 *
 * Regions and RC areas are fixed structure and live here. Cities are seeded
 * into the database (see server/src/db/seed) because the team edits them.
 */
export const REGIONS = ['east', 'midwest', 'west'];
export const REGION_NAMES = {
    east: 'East',
    midwest: 'Midwest',
    west: 'West',
};
/**
 * IDs are stored in the database (user and city rows), so an existing id is
 * never renamed or removed here — only added, or kept with new display text.
 */
export const RC_AREAS = [
    'ne1',
    'ne2',
    'southeast',
    'east-canada',
    'mw-north',
    'mw-south',
    'ca-hi',
    'west-other',
    'west-canada',
];
export const RC_AREA_INFO = {
    ne1: { id: 'ne1', region: 'east', name: 'East · North East 1', shortName: 'North East 1', home: 'Philadelphia' },
    ne2: { id: 'ne2', region: 'east', name: 'East · North East 2', shortName: 'North East 2', home: 'Boston' },
    southeast: { id: 'southeast', region: 'east', name: 'East · Southeast', shortName: 'Southeast', home: 'Charlotte' },
    'east-canada': {
        id: 'east-canada',
        region: 'east',
        name: 'East · East Canada',
        shortName: 'East Canada',
        home: 'Toronto',
    },
    'mw-north': { id: 'mw-north', region: 'midwest', name: 'Midwest · North', shortName: 'Midwest North', home: 'Chicago' },
    'mw-south': { id: 'mw-south', region: 'midwest', name: 'Midwest · South', shortName: 'Midwest South', home: 'St. Louis' },
    'ca-hi': {
        id: 'ca-hi',
        region: 'west',
        name: 'West · CA + AK + HI',
        shortName: 'CA + AK + HI',
        home: 'Los Angeles',
    },
    'west-other': {
        id: 'west-other',
        region: 'west',
        name: 'West · Texas + other west',
        shortName: 'Texas & other west',
        home: 'Dallas',
    },
    'west-canada': {
        id: 'west-canada',
        region: 'west',
        name: 'West · West Canada',
        shortName: 'West Canada',
        home: 'Vancouver',
    },
};
export function rcAreasInRegion(region) {
    return RC_AREAS.map((id) => RC_AREA_INFO[id]).filter((a) => a.region === region);
}
/**
 * The zones a North American city can plausibly keep time in, for the picker
 * when the central team adds one. The server accepts any valid IANA zone, so
 * an unusual case can still be set over the API; this list is UX, not policy.
 */
export const NA_TIMEZONES = [
    { id: 'America/St_Johns', label: 'Newfoundland — St. John’s' },
    { id: 'America/Halifax', label: 'Atlantic — Halifax' },
    { id: 'America/New_York', label: 'Eastern — New York' },
    { id: 'America/Toronto', label: 'Eastern — Toronto' },
    { id: 'America/Chicago', label: 'Central — Chicago' },
    { id: 'America/Winnipeg', label: 'Central — Winnipeg' },
    { id: 'America/Mexico_City', label: 'Central — Mexico City' },
    { id: 'America/Monterrey', label: 'Central — Monterrey' },
    { id: 'America/Regina', label: 'Saskatchewan — Regina (no DST)' },
    { id: 'America/Denver', label: 'Mountain — Denver' },
    { id: 'America/Edmonton', label: 'Mountain — Edmonton' },
    { id: 'America/Phoenix', label: 'Arizona — Phoenix (no DST)' },
    { id: 'America/Los_Angeles', label: 'Pacific — Los Angeles' },
    { id: 'America/Vancouver', label: 'Pacific — Vancouver' },
    { id: 'America/Anchorage', label: 'Alaska — Anchorage' },
    { id: 'Pacific/Honolulu', label: 'Hawaii — Honolulu (no DST)' },
];
/* ------------------------------------------------------------------ roles */
/**
 * What someone is allowed to do. One user has exactly one role; their scope
 * (city / rc area / region) comes from the columns on the user row.
 */
export const ROLES = ['volunteer', 'city_coordinator', 'regional_coordinator', 'central', 'admin'];
export const ROLE_NAMES = {
    volunteer: 'Volunteer',
    city_coordinator: 'City coordinator',
    regional_coordinator: 'Regional coordinator',
    central: 'Central team',
    admin: 'Administrator',
};
/**
 * Reads the Role cell of an imported spreadsheet. People write these by hand,
 * so: any casing, with or without "coordinator", and the abbreviations the
 * team actually uses. Blank is not an error — it means "the default", which
 * the caller decides (Volunteer). Administrators are never made by import.
 */
export function parseImportRole(text) {
    const t = text.trim().toLowerCase().replace(/[\s_-]+/g, ' ');
    if (!t)
        return 'blank';
    if (['volunteer', 'vol', 'promo volunteer'].includes(t))
        return 'volunteer';
    if (['city coordinator', 'city', 'cc', 'city coord'].includes(t))
        return 'city_coordinator';
    if (['regional coordinator', 'regional', 'rc', 'region coordinator', 'regional coord'].includes(t)) {
        return 'regional_coordinator';
    }
    if (['central', 'central team', 'central coordinator', 'central team member'].includes(t))
        return 'central';
    return null;
}
/** Roles that get into the coordinator console at all. */
export const COORDINATOR_ROLES = ['city_coordinator', 'regional_coordinator', 'central', 'admin'];
export function isCoordinator(role) {
    return COORDINATOR_ROLES.includes(role);
}
/**
 * Ranked so a check reads "does this person sit at or above X". Volunteers are
 * 0; admin is central plus the right to manage accounts.
 */
export const ROLE_RANK = {
    volunteer: 0,
    city_coordinator: 1,
    regional_coordinator: 2,
    central: 3,
    admin: 4,
};
/** Which roles a given role may invite. A city coordinator cannot mint an RC. */
export const CAN_INVITE = {
    volunteer: [],
    city_coordinator: ['volunteer'],
    regional_coordinator: ['volunteer', 'city_coordinator'],
    central: ['volunteer', 'city_coordinator', 'regional_coordinator', 'central'],
    admin: ['volunteer', 'city_coordinator', 'regional_coordinator', 'central', 'admin'],
};
/* ----------------------------------------------------------------- skills */
/**
 * What a volunteer can help with. Tasks are matched to volunteers on these.
 * Wording is the team's, carried over from the prototype.
 */
export const SKILLS = [
    'people',
    'drive',
    'design',
    'social',
    'writing',
    'photography',
    'booths',
    'flyering',
    'calling',
    'press',
];
export const SKILL_NAMES = {
    people: 'Good with people',
    drive: 'Can drive & carry',
    design: 'Design & posters',
    social: 'Social media',
    writing: 'Writing',
    photography: 'Photography',
    booths: 'Events & booths',
    flyering: 'Flyering & posters',
    calling: 'Calling',
    press: 'Press & outreach',
};
/** The subset offered as "what you'd like to learn" — marketing skills only. */
export const LEARNABLE_SKILLS = ['social', 'design', 'writing', 'booths', 'photography'];
/* ------------------------------------------------------------------ tasks */
export const TASK_TIMES = ['15 min', '30 min', '1 hr', '2–3 hrs', 'Half day'];
export const TASK_FREQS = ['Once', 'Daily', 'Weekly', 'Monthly'];
export const TASK_AREAS = ['events', 'flyering', 'calling', 'social', 'press', 'design'];
export const TASK_AREA_NAMES = {
    events: 'Events & booths',
    flyering: 'Flyering & posters',
    calling: 'Calling',
    social: 'Social media',
    press: 'Press & outreach',
    design: 'Design & posters',
};
export const TASK_STATUSES = ['draft', 'open', 'closed', 'cancelled'];
/**
 * A volunteer's relationship to one task.
 *  accepted  — said yes, hasn't done it
 *  completed — done, possibly with proof
 *  declined  — said no; reversible ("I can help after all")
 *  withdrawn — accepted then pulled out
 */
export const ASSIGNMENT_STATUSES = ['accepted', 'completed', 'declined', 'withdrawn'];
/* -------------------------------------------------------------- audience */
/** Where a task is published. Mirrors the console's two audience axes. */
export const AUDIENCE_WHERE = ['city', 'cities', 'rc_area', 'region', 'na'];
/** Who inside that geography sees it. */
export const AUDIENCE_WHO = ['all', 'skill', 'handpick', 'one', 'city_coordinators', 'regional_coordinators'];
/* ------------------------------------------------------------- invites */
export const INVITE_STATUSES = ['pending', 'accepted', 'revoked', 'expired'];
/** How long an invite link stays good for. */
export const INVITE_TTL_DAYS = 21;
//# sourceMappingURL=domain.js.map